import { useEffect, useRef, useState } from "react"

import { Box, Stack } from "@mui/material"
import { isEmpty } from "lodash"
import { FormProvider } from "react-hook-form"

import { useSelector } from "@ggs/store"
import type { FC } from ".."
import {
  Container,
  Pagination,
  SearchFacets,
  Text,
  TripCollection,
  TripItinerariesSearchForm,
} from ".."
import { useTripSearchState, useTripsSearchData } from "../../local/hooks"
import { getSortOption } from "../TripGeneralSearch/helpers"

type TripCategorySearchProps = {
  tripStyles?: string
  tripTypes?: string
  path: string
  hideFilters: string[]
}

const TripCategorySearch: FC<TripCategorySearchProps> = ({
  tripStyles,
  tripTypes,
  path,
  hideFilters = [],
}) => {
  const { getTripsSolrDataByFilters } = useTripsSearchData()
  const userCountry = useSelector((state) => state.settings.country)
  const listRef = useRef<HTMLDivElement>(null)
  const [tripSearchResults, setTripSearchResults] = useState<any>({
    count: 0,
    totalPages: 0,
    results: null,
    countries: [],
    locations: [],
    experiences: [],
    prices: [],
    durations: [],
  })

  const { form, handlers, state } = useTripSearchState({
    path,
    defaultState: {
      ...(tripStyles && { tripStyles }),
      ...(tripTypes && { tripTypes }),
    },
    ignoreFilters: hideFilters,
  })

  const fetchSolrTripsData = async (params: any) => {
    const data = await getTripsSolrDataByFilters(params)
    setTripSearchResults(data)
  }

  const params = state?.urlParams
  const searchParams = state?.searchState
  // Resolve Solr data.
  useEffect(() => {
    fetchSolrTripsData(searchParams)
  }, [searchParams, userCountry])

  const handleScrollToTop = () => {
    if (listRef.current) {
      const listItemRect = listRef.current.getBoundingClientRect()
      const topOffset = window.scrollY + listItemRect.top - 150
      window.scrollTo({ top: topOffset, behavior: "smooth" })
    }
  }

  return (
    <FormProvider {...form}>
      <Container maxWidth={1560}>
        <Stack
          rowGap={{ xs: 15, md: 25 }}
          sx={(theme) => ({
            pt: { xs: 15, md: 30 },
            pb: { xs: 2, md: 2 },
            position: "relative",
          })}
        >
          <TripItinerariesSearchForm
            locations={tripSearchResults.locations}
            experiences={tripSearchResults.experiences}
            countries={tripSearchResults.countries}
            onApplyFilter={handlers.onApplyFilter}
            searchParams={searchParams}
            hideFilters={hideFilters}
          />
        </Stack>
      </Container>
      <Container maxWidth={1480}>
        <Box pt={5} pb={15}>
          <SearchFacets
            facets={state.usedFilters}
            sortOptions={[
              { label: "Duration, Short to Long", type: "its_duration-asc" },
              { label: "Duration, Long to Short", type: "its_duration-desc" },
              { label: "Price, Low to High", type: "price-asc" },
              { label: "Price, High to Low", type: "price-desc" },
            ]}
            count={tripSearchResults.count}
            removeAllFilters={handlers.removeAllFilters}
            removeIndividualFilter={handlers.removeIndividualFilter}
            sortChange={handlers.onSortChange}
            paramSort={getSortOption(params.sort as string)}
            onSpecialsToggle={handlers.onSpecialsToggle}
            isSpecialsChecked={params.specials}
          />
        </Box>
        <Box ref={listRef}>
          {tripSearchResults.results !== null && isEmpty(tripSearchResults.results) ? (
            <Text title="No results found" text="Please review your search criteria." />
          ) : (
            <TripCollection
              items={tripSearchResults.results}
              showSpecialTag={Boolean(params.specials)}
            />
          )}
        </Box>
        <Box pt={{ xs: 19, md: 20 }} pb={{ xs: 15, md: 45 }}>
          <Pagination
            page={parseInt(params.page as string) || 1}
            count={tripSearchResults.totalPages}
            onPageChange={(page: number) => handlers.onPageChange(page, handleScrollToTop)}
          />
        </Box>
      </Container>
    </FormProvider>
  )
}

export default TripCategorySearch
